import React from 'react';
import PropTypes from 'prop-types';
import StatCard from 'components/StatCard/StatCard';
import HelpTip from './HelpTip';
import { formatNumber, relativeAge } from './datasetMonitorFormat';

/**
 * What each card actually counts, in the client's terms rather than the
 * backend's. Keyed by card so the wording lives in one place.
 */
const CARD_TOOLTIP = {
	coverage:
		'Active properties whose last successful run for this dataset falls inside the window, out of every active property. Below 80% is reported as Degraded coverage.',
	recordsAdded: 'New rows this dataset wrote into its own table during the window.',
	recordsChanged: 'Existing rows this dataset updated during the window.',
	watchers:
		'Portfolios subscribed to this dataset\'s notification type. Zero means no emails are expected from it at all.',
	failedRuns:
		'Runs of this dataset that errored inside the window, out of every run. A property is failing when its latest run errored and it has not succeeded since.',
	failedSends:
		'Alert, notification, reminder and expiring emails for this dataset that failed to send inside the window. The per-type table below counts every send ever, not just this window.',
};

const EMAIL_TYPE_ROWS = [
	{ key: 'alert', label: 'Alert' },
	{ key: 'notification', label: 'Notification' },
	{ key: 'reminder', label: 'Reminder' },
	{ key: 'expiring', label: 'Expiring' },
];

/**
 * DatasetStatCards
 *
 * The `show` drilldown overview (design spec 10/12): six summary cards
 * (coverage, records added, records changed, watchers, failed sends, failed runs), each
 * explaining what it counts through its own HelpTip, plus the four email types
 * in full.
 *
 * Suppressed was removed at the client's request as a term they could not
 * interpret. The backend still reports it and the health rules still read it. Reminder and Expiring are
 * dropped from the main table (a dead "no sends" column in the client's
 * own screenshot) but nothing is lost - both are shown here alongside
 * Alert and Notification.
 *
 * `hours` mirrors the container's current reporting window (review finding:
 * this card used to hardcode "Coverage 24h" even after the table's own
 * window - and its "Coverage <hours>h" header - moved elsewhere); it is
 * threaded down from DatasetDrawer, which requests the drilldown for this
 * same window, so the fetch and the title can never disagree.
 */
const DatasetStatCards = ({ dataset, hours }) => {
	if (!dataset) {
		return <p className="text-muted">No drilldown data yet.</p>;
	}

	const coverage = dataset.coverage || {};
	const ingested = dataset.ingested || {};
	const emails = dataset.emails || {};
	// Window-scoped, from its own payload field: the per-type failed counts in
	// the table below are all-time, and summing those would keep this card lit
	// for a failure from months ago while the status reads Healthy.
	const totalFailed = dataset.emails_failed_window || 0;
	const runs = dataset.runs || {};
	const runsFailed = runs.failed || 0;
	const runsTotal = (runs.ok || 0) + runsFailed;
	const recentErrors = Array.isArray(dataset.recent_errors) ? dataset.recent_errors : [];

	return (
		<div className="dataset-stat-cards">
			<div className="row g-3">
				<div className="col-md-4 col-6">
					<div className="dataset-stat-card-wrap">
						<StatCard
							title={`Coverage ${hours}h`}
							value={`${formatNumber(coverage.ran || 0)} / ${formatNumber(coverage.expected || 0)}`}
							icon="bi bi-pie-chart"
						/>
						<HelpTip label={`Coverage ${hours}h`} text={CARD_TOOLTIP.coverage} />
					</div>
				</div>
				<div className="col-md-4 col-6">
					<div className="dataset-stat-card-wrap">
						<StatCard
							title="Records added"
							value={formatNumber(ingested.added || 0)}
							icon="bi bi-plus-circle"
						/>
						<HelpTip label="Records added" text={CARD_TOOLTIP.recordsAdded} />
					</div>
				</div>
				<div className="col-md-4 col-6">
					<div className="dataset-stat-card-wrap">
						<StatCard
							title="Records changed"
							value={formatNumber(ingested.updated || 0)}
							icon="bi bi-arrow-repeat"
						/>
						<HelpTip label="Records changed" text={CARD_TOOLTIP.recordsChanged} />
					</div>
				</div>
				<div className="col-md-4 col-6">
					<div className="dataset-stat-card-wrap">
						<StatCard
							title="Watchers"
							value={formatNumber(dataset.watchers || 0)}
							icon="bi bi-eye"
						/>
						<HelpTip label="Watchers" text={CARD_TOOLTIP.watchers} />
					</div>
				</div>
				<div className="col-md-4 col-6">
					<div className="dataset-stat-card-wrap">
						<StatCard
							title="Failed sends"
							value={formatNumber(totalFailed)}
							icon="bi bi-exclamation-triangle"
						/>
						<HelpTip label="Failed sends" text={CARD_TOOLTIP.failedSends} />
					</div>
				</div>
				<div className="col-md-4 col-6">
					<div className="dataset-stat-card-wrap">
						<StatCard
							title="Failed runs"
							value={`${formatNumber(runsFailed)} / ${formatNumber(runsTotal)}`}
							icon="bi bi-x-octagon"
						/>
						<HelpTip label="Failed runs" text={CARD_TOOLTIP.failedRuns} />
						{runs.failing_properties > 0 && (
							<div className="text-danger small mt-1">
								{formatNumber(runs.failing_properties)} properties failing
							</div>
						)}
					</div>
				</div>
			</div>

			{recentErrors.length > 0 && (
				<>
					<h6 className="mt-4 mb-2">Recent errors</h6>
					<div className="table-responsive">
						<table className="table table-sm dataset-recent-errors-table">
							<thead className="table-light">
								<tr>
									<th>Error</th>
									<th className="text-end">Properties</th>
									<th>Last seen</th>
								</tr>
							</thead>
							<tbody>
								{recentErrors.map((row) => (
									<tr key={row.error}>
										<td className="text-break">{row.error}</td>
										<td className="text-end">{formatNumber(row.properties || 0)}</td>
										<td>{relativeAge(row.last_errored_at)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			)}

			<h6 className="mt-4 mb-2">Email types</h6>
			<div className="table-responsive">
				<table className="table table-sm dataset-email-types-table">
					<thead className="table-light">
						<tr>
							<th>Type</th>
							<th>Last sent</th>
							<th className="text-end">Sent</th>
							<th className="text-end">Failed</th>
						</tr>
					</thead>
					<tbody>
						{EMAIL_TYPE_ROWS.map(({ key, label }) => {
							const email = emails[key] || {};
							return (
								<tr key={key}>
									<td>{label}</td>
									<td>
										{dataset.emits_emails ? relativeAge(email.last_sent_at) : 'N/A'}
									</td>
									<td className="text-end">{formatNumber(email.count || 0)}</td>
									<td className="text-end">
										{email.failed > 0 ? (
											<span className="badge bg-danger">{email.failed}</span>
										) : (
											formatNumber(email.failed || 0)
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

DatasetStatCards.propTypes = {
	dataset: PropTypes.object,
	hours: PropTypes.number,
};

DatasetStatCards.defaultProps = {
	dataset: null,
	hours: 24,
};

export default DatasetStatCards;
